import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, RotateCcw } from "lucide-react";
import { fmtCAD } from "@/lib/currency";
import { updateOrderInvoice } from "@/lib/api";

const GST_RATE = 0.05;
const PST_RATE_SK = 0.06;

function toLine(li) {
  return {
    product_id: li.product_id || null,
    name: li.name || "",
    sku: li.sku || "",
    size: li.size || "",
    quantity: Number(li.quantity) || 0,
    unit_wholesale_price: Number(li.unit_wholesale_price) || 0,
  };
}

export default function InvoiceEditor({ order, onSaved }) {
  const [items, setItems] = useState(() => (order.items || []).map(toLine));
  const [shipping, setShipping] = useState(Number(order.shipping_cost) || 0);
  const [discountPct, setDiscountPct] = useState(Number(order.discount_pct) || 0);
  const [saving, setSaving] = useState(false);

  // Reset local state if the underlying order prop changes (e.g. after save).
  useEffect(() => {
    setItems((order.items || []).map(toLine));
    setShipping(Number(order.shipping_cost) || 0);
    setDiscountPct(Number(order.discount_pct) || 0);
  }, [order.id, order.items, order.shipping_cost, order.discount_pct]);

  const totals = useMemo(() => {
    const units = items.reduce((s, li) => s + (Number(li.quantity) || 0), 0);
    const subtotal = items.reduce((s, li) => s + (Number(li.unit_wholesale_price) || 0) * (Number(li.quantity) || 0), 0);
    const discount = subtotal * (Math.max(0, Math.min(100, Number(discountPct) || 0)) / 100);
    const taxable = Math.max(0, subtotal - discount + (Number(shipping) || 0));
    const gst = taxable * GST_RATE;
    const isSK = (order.province || "").toUpperCase() === "SK";
    const pst = isSK ? taxable * PST_RATE_SK : 0;
    const total = taxable + gst + pst;
    return { units, subtotal, discount, taxable, gst, pst, total, isSK };
  }, [items, shipping, discountPct, order.province]);

  const setLine = (idx, field, val) =>
    setItems((arr) => arr.map((li, i) => (i === idx ? { ...li, [field]: val } : li)));

  const removeLine = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const addLine = () =>
    setItems((arr) => [
      ...arr,
      { product_id: null, name: "", sku: "", size: "", quantity: 1, unit_wholesale_price: 0 },
    ]);

  const reset = () => {
    setItems((order.items || []).map(toLine));
    setShipping(Number(order.shipping_cost) || 0);
    setDiscountPct(Number(order.discount_pct) || 0);
  };

  const save = async () => {
    const cleaned = items
      .map((li) => ({
        ...li,
        quantity: Math.max(0, Math.round(Number(li.quantity) || 0)),
        unit_wholesale_price: Math.max(0, Number(li.unit_wholesale_price) || 0),
      }))
      .filter((li) => li.name.trim() && li.quantity > 0);
    if (cleaned.length === 0) {
      toast.error("Add at least one line item with name and quantity.");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateOrderInvoice(order.id, {
        items: cleaned,
        shipping_cost: Number(shipping) || 0,
        discount_pct: Math.max(0, Math.min(100, Number(discountPct) || 0)),
      });
      toast.success("Invoice updated");
      onSaved && onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save invoice");
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    JSON.stringify(items) !== JSON.stringify((order.items || []).map(toLine)) ||
    Number(shipping) !== Number(order.shipping_cost || 0) ||
    Number(discountPct) !== Number(order.discount_pct || 0);

  const cellInput = "w-full px-2 py-1.5 text-sm border border-border rounded bg-background focus:border-primary focus:outline-none";

  return (
    <div data-testid={`invoice-editor-${order.id}`} className="rounded-xl border border-border overflow-hidden">
      <div className="px-4 py-2.5 bg-background border-b border-border flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Invoice editor</p>
        {dirty && <span className="text-[11px] text-amber-600 font-medium">Unsaved changes</span>}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-background/40">
            <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              <th className="px-3 py-2 w-[40%]">Product</th>
              <th className="px-3 py-2">SKU</th>
              <th className="px-3 py-2">Size</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Unit price</th>
              <th className="px-3 py-2 text-right">Line total</th>
              <th className="px-3 py-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((li, idx) => {
              const lineTotal = (Number(li.unit_wholesale_price) || 0) * (Number(li.quantity) || 0);
              return (
                <tr key={idx} className="border-t border-border">
                  <td className="px-3 py-1.5">
                    <input
                      value={li.name}
                      onChange={(e) => setLine(idx, "name", e.target.value)}
                      placeholder="Item name"
                      className={cellInput}
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      value={li.sku || ""}
                      onChange={(e) => setLine(idx, "sku", e.target.value)}
                      placeholder="—"
                      className={`${cellInput} font-mono text-xs`}
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      value={li.size || ""}
                      onChange={(e) => setLine(idx, "size", e.target.value)}
                      placeholder="—"
                      className={cellInput}
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={li.quantity}
                      onChange={(e) => setLine(idx, "quantity", e.target.value)}
                      className={`${cellInput} text-right`}
                    />
                  </td>
                  <td className="px-3 py-1.5">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={li.unit_wholesale_price}
                      onChange={(e) => setLine(idx, "unit_wholesale_price", e.target.value)}
                      className={`${cellInput} text-right`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtCAD(lineTotal)}</td>
                  <td className="px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      aria-label="Remove line"
                      className="p-1 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 size={14} strokeWidth={1.6} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-3 py-2 border-t border-border">
        <button
          type="button"
          onClick={addLine}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <Plus size={13} strokeWidth={1.75} /> Add line item
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 py-4 border-t border-border bg-secondary/30">
        <div className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground block mb-1">Shipping cost</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={shipping}
              onChange={(e) => setShipping(e.target.value)}
              className="w-40 px-2.5 py-1.5 text-sm border border-border rounded bg-background focus:border-primary focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground block mb-1">Discount (%)</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={discountPct}
              onChange={(e) => setDiscountPct(e.target.value)}
              className="w-40 px-2.5 py-1.5 text-sm border border-border rounded bg-background focus:border-primary focus:outline-none"
            />
          </label>
        </div>

        <div className="md:justify-self-end w-full md:w-72 space-y-1 text-sm">
          <Row label="Units" value={String(totals.units)} />
          <Row label="Subtotal" value={fmtCAD(totals.subtotal)} />
          {totals.discount > 0 && (
            <Row label={`Discount (${Number(discountPct) || 0}%)`} value={`− ${fmtCAD(totals.discount)}`} />
          )}
          {Number(shipping) > 0 && <Row label="Shipping" value={fmtCAD(Number(shipping) || 0)} />}
          <Row label="GST (5%)" value={fmtCAD(totals.gst)} />
          {totals.isSK && <Row label="PST (6%)" value={fmtCAD(totals.pst)} />}
          <div className="flex justify-between font-serif text-base border-t border-border pt-1 mt-1">
            <span>Total</span>
            <span className="tabular-nums">{fmtCAD(totals.total)}</span>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={reset}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider border border-border rounded hover:bg-secondary disabled:opacity-40"
        >
          <RotateCcw size={13} strokeWidth={1.75} /> Reset
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid={`invoice-save-${order.id}`}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-wider rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          <Save size={13} strokeWidth={1.75} /> {saving ? "Saving…" : "Save invoice"}
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
